import type { DayId } from './calendar.ts'
import type {
  Actor,
  Adaptations,
  DomainEvent,
  Moment,
  RewardLadder,
  ScreenLimits,
  Step,
} from './events.ts'
import type { FamilyId, MemberId, RewardId, StepId, TaskId } from './ids.ts'

/**
 * The house as the study describes it: three routines, ten missions and eight
 * rewards. This is what the family starts from on day one, and every bit of it
 * is meant to be changed in the weekly meeting once real life disagrees.
 *
 * Two rules are built into the shape of this file rather than left to care.
 * Routines pay no coins on any rung, because a routine is contribution and not
 * work. And at least half of the catalogue is not screen time, because if the
 * only thing worth buying is the tablet, the tablet wins.
 */

export interface ChildSetup {
  readonly id: MemberId
  readonly name: string
  readonly birthYear: number
  readonly accent: string
  readonly emoji: string
  /** No irony, no "almost", changes announced in advance, no surprise chests. */
  readonly literalLanguage: boolean
  /** Unlocks the food-autonomy missions. Never affects the mechanics. */
  readonly glutenFree: boolean
}

export interface AdultSetup {
  readonly id: MemberId
  readonly name: string
}

export interface SeedOptions {
  readonly familyId: FamilyId
  /**
   * Quién queda como autor de la semilla. Es el primero de los adultos.
   */
  readonly parentId: MemberId
  readonly parentName: string
  /**
   * Los demás adultos de la casa, si hay más de uno.
   *
   * Existe porque una casa puede tener dos madres, dos padres, uno de cada, uno
   * solo, o una abuela. El modelo ya lo admitía —`role: 'parent'` no es un
   * número— y lo que faltaba era que la semilla los escribiera.
   */
  readonly otherAdults?: readonly AdultSetup[]
  readonly timezone: string
  readonly dayCutoffHour: number
  readonly meetingDayOfWeek: number
  readonly children: readonly ChildSetup[]
  readonly now: Date
  readonly day: DayId
}

/** A mission's pay by rung. It drops as the task is mastered, and that is the point. */
export function missionLadder(coins: number, xp: number): RewardLadder {
  return {
    1: { coins, xp },
    2: { coins, xp },
    3: { coins: Math.ceil(coins / 2), xp },
    4: { coins: 0, xp: Math.ceil(xp / 2) },
  }
}

/** A routine's pay by rung. Zero coins, always. */
export function routineLadder(xp: number): RewardLadder {
  return {
    1: { coins: 0, xp },
    2: { coins: 0, xp },
    3: { coins: 0, xp },
    4: { coins: 0, xp: 0 },
  }
}

interface RoutineTemplate {
  readonly key: string
  readonly moment: Moment
  readonly name: string
  readonly emoji: string
  readonly xp: number
  readonly steps: readonly string[]
}

/** One routine per moment of the day, five steps at most, as the study asks. */
export const ROUTINES: readonly RoutineTemplate[] = [
  {
    key: 'manana',
    moment: 'morning',
    name: 'Rutina de la mañana',
    emoji: '🌅',
    xp: 15,
    steps: [
      'Hacer la cama',
      'Vestirse y dejar el pijama doblado',
      'Desayunar',
      'Llevar el plato al lavavajillas',
      'Lavarse los dientes',
    ],
  },
  {
    key: 'tarde',
    moment: 'afternoon',
    name: 'Rutina de la tarde',
    emoji: '🎒',
    xp: 20,
    steps: [
      'Vaciar la mochila',
      'Dejar lista la mochila de mañana',
      'Recoger lo que has usado',
      'Ropa al cesto',
    ],
  },
  {
    key: 'noche',
    moment: 'evening',
    name: 'Rutina de la noche',
    emoji: '🌙',
    xp: 15,
    steps: [
      'Ponerse el pijama',
      'Lavarse los dientes',
      'Dejar preparada la ropa de mañana',
      'Entregar la tablet',
    ],
  },
]

interface MissionTemplate {
  readonly key: string
  readonly name: string
  readonly emoji: string
  readonly coins: number
  readonly xp: number
  readonly difficulty: number
  readonly steps: readonly string[]
  readonly requiresPhoto?: boolean
  /** Only offered to a child who needs it. */
  readonly glutenFreeOnly?: boolean
}

export const MISSIONS: readonly MissionTemplate[] = [
  {
    key: 'mesa',
    name: 'Poner la mesa',
    emoji: '🍽️',
    coins: 5,
    xp: 10,
    difficulty: 1,
    steps: ['Manteles y platos', 'Cubiertos y vasos', 'Agua y pan'],
  },
  {
    key: 'basura',
    name: 'Sacar la basura o el reciclaje',
    emoji: '🗑️',
    coins: 5,
    xp: 10,
    difficulty: 1,
    steps: ['Cerrar la bolsa', 'Bajarla al contenedor', 'Poner una bolsa nueva'],
  },
  {
    key: 'plantas',
    name: 'Regar las plantas',
    emoji: '🪴',
    coins: 5,
    xp: 10,
    difficulty: 1,
    steps: ['Llenar la regadera', 'Regar las del salón', 'Regar las de la terraza'],
  },
  {
    key: 'lavavajillas',
    name: 'Vaciar el lavavajillas',
    emoji: '🍴',
    coins: 10,
    xp: 20,
    difficulty: 2,
    steps: ['Platos y vasos a su sitio', 'Cubiertos al cajón', 'Cerrar el lavavajillas'],
  },
  {
    key: 'ropa',
    name: 'Doblar y guardar tu ropa',
    emoji: '👕',
    coins: 10,
    xp: 20,
    difficulty: 2,
    steps: ['Doblar las camisetas', 'Doblar los pantalones', 'Guardar todo en el armario'],
  },
  {
    key: 'desayuno-sin-gluten',
    name: 'Preparar tu desayuno sin gluten',
    emoji: '🥣',
    coins: 10,
    xp: 20,
    difficulty: 2,
    glutenFreeOnly: true,
    steps: [
      'Comprobar el símbolo sin gluten en el envase',
      'Usar tu tostadora y tus utensilios',
      'Preparar el desayuno',
      'Recoger y limpiar la encimera',
    ],
  },
  {
    key: 'aspirar',
    name: 'Aspirar el salón',
    emoji: '🧹',
    coins: 15,
    xp: 30,
    difficulty: 3,
    steps: ['Subir las sillas', 'Aspirar el suelo', 'Guardar la aspiradora'],
  },
  {
    key: 'cocinar',
    name: 'Ayudar a cocinar la cena',
    emoji: '🥘',
    coins: 15,
    xp: 30,
    difficulty: 3,
    steps: ['Lavarse las manos', 'Preparar los ingredientes', 'Cocinar con un adulto'],
  },
  {
    key: 'habitacion',
    name: 'Ordenar la habitación a fondo',
    emoji: '🛏️',
    coins: 20,
    xp: 40,
    difficulty: 3,
    requiresPhoto: true,
    steps: [
      'Libros en la estantería',
      'Ropa en el cesto o en el armario',
      'Juguetes en su caja',
      'Suelo despejado',
      'Mesa vacía',
    ],
  },
  {
    key: 'cierre',
    name: 'Cerrar la tablet antes de que se acabe',
    emoji: '⏱️',
    coins: 3,
    xp: 5,
    difficulty: 1,
    steps: ['Guardar la partida', 'Cerrar el juego', 'Entregar la tablet'],
  },
]

interface RewardTemplate {
  readonly key: string
  readonly name: string
  readonly price: number
  readonly kind: 'screen' | 'privilege' | 'family' | 'social' | 'big'
  readonly notes?: string
  readonly limit?: { readonly per: 'day' | 'week'; readonly max: number }
}

export const REWARDS: readonly RewardTemplate[] = [
  {
    key: 'minutos',
    name: 'Cinco minutos más de tablet',
    price: 5,
    kind: 'screen',
    notes: 'Hasta el tope del día. Un adulto los concede en Family Link.',
  },
  {
    key: 'tarde',
    name: 'Acostarse veinte minutos más tarde',
    price: 25,
    kind: 'privilege',
    notes: 'Viernes o sábado, con algo tranquilo y sin pantalla.',
    limit: { per: 'week', max: 1 },
  },
  {
    key: 'pelicula',
    name: 'Elegir la película del viernes',
    price: 30,
    kind: 'family',
    notes: 'Si la compran los dos, se turnan.',
    limit: { per: 'week', max: 1 },
  },
  {
    key: 'plan',
    name: 'Elegir el plan del sábado por la mañana',
    price: 40,
    kind: 'family',
    notes: 'De la lista que acordéis en la reunión.',
    limit: { per: 'week', max: 1 },
  },
  {
    key: 'vale',
    name: 'Hoy no hago mi misión',
    price: 50,
    kind: 'privilege',
    notes: 'Solo misiones, nunca rutinas.',
    limit: { per: 'week', max: 1 },
  },
  {
    key: 'amigo',
    name: 'Invitar a un amigo a casa',
    price: 60,
    kind: 'social',
    notes: 'Con merienda segura para todos.',
  },
  {
    key: 'salida',
    name: 'Una salida especial',
    price: 150,
    kind: 'big',
    notes: 'Hay que ahorrar dos o tres semanas.',
  },
  {
    key: 'grande',
    name: 'Un juego, un libro o material de lo que te gusta',
    price: 250,
    kind: 'big',
    notes: 'Ahorro largo. Se celebra en la reunión.',
  },
]

// --- Building the events -----------------------------------------------------

function toSteps(taskKey: string, texts: readonly string[]): Step[] {
  return texts.map((text, index) => ({ id: `${taskKey}-p${index + 1}` as StepId, text }))
}

/** Lo que trae puesto cualquier miembro nuevo. */
export const DEFAULT_LIMITS: ScreenLimits = {
  base: { weekday: 30, weekend: 45 },
  extraCap: { weekday: 15, weekend: 30 },
  blockMinutes: 5,
}

export function adaptationsFor(literalLanguage: boolean): Adaptations {
  return {
    literalLanguage,
    noSurprises: literalLanguage,
    reducedMotion: false,
    largeText: false,
    readAloud: false,
    promotionNoticeDays: literalLanguage ? 2 : 0,
  }
}

/** La cabecera común de todos los eventos de una tacada. */
export interface SeedHead {
  readonly v: 1
  readonly at: string
  readonly day: DayId
  readonly actor: Actor
}

/**
 * Todo lo que trae un hijo: su alta, sus rutinas y sus misiones.
 *
 * Está fuera de `buildSeed` para que dar de alta a un hijo **después** —porque
 * llega uno, o porque la casa se montó con dos y son tres— traiga exactamente lo
 * mismo que traía al crear la casa. Un hijo añadido a mano y sin rutinas deja al
 * padre escribiéndolas una por una, que es justo lo que esta semilla evita.
 */
export function childEvents(child: ChildSetup, head: SeedHead): DomainEvent[] {
  const events: DomainEvent[] = [
    {
      ...head,
      type: 'MemberAdded',
      payload: {
        memberId: child.id,
        name: child.name,
        role: 'child',
        birthYear: child.birthYear,
        accent: child.accent,
        emoji: child.emoji,
        adaptations: adaptationsFor(child.literalLanguage),
        limits: DEFAULT_LIMITS,
        glutenFree: child.glutenFree,
      },
    },
  ]

  for (const routine of ROUTINES) {
    const taskId = `${child.id}-${routine.key}` as TaskId
    events.push({
      ...head,
      type: 'TaskDefined',
      payload: {
        taskId,
        memberId: child.id,
        kind: 'routine',
        moment: routine.moment,
        name: routine.name,
        emoji: routine.emoji,
        steps: toSteps(taskId, routine.steps),
        difficulty: 1,
        rewards: routineLadder(routine.xp),
        promotionThreshold: 10,
        requiresPhoto: false,
      },
    })
  }

  for (const mission of MISSIONS) {
    if (mission.glutenFreeOnly && !child.glutenFree) continue
    const taskId = `${child.id}-${mission.key}` as TaskId
    events.push({
      ...head,
      type: 'TaskDefined',
      payload: {
        taskId,
        memberId: child.id,
        kind: 'mission',
        name: mission.name,
        emoji: mission.emoji,
        steps: toSteps(taskId, mission.steps),
        difficulty: mission.difficulty,
        rewards: missionLadder(mission.coins, mission.xp),
        promotionThreshold: 10,
        requiresPhoto: mission.requiresPhoto ?? false,
      },
    })
  }

  return events
}

/** El alta de un adulto. No trae tareas: los adultos no las tienen. */
export function adultEvent(adult: AdultSetup, head: SeedHead): DomainEvent {
  return {
    ...head,
    type: 'MemberAdded',
    payload: {
      memberId: adult.id,
      name: adult.name,
      role: 'parent',
      accent: '#55676a',
      adaptations: adaptationsFor(false),
      limits: DEFAULT_LIMITS,
      glutenFree: false,
    },
  }
}

export function buildSeed(options: SeedOptions): DomainEvent[] {
  const at = options.now.toISOString()
  const actor: Actor = { kind: 'parent', id: options.parentId }
  const head = { v: 1, at, day: options.day, actor } as const
  const events: DomainEvent[] = []

  events.push({
    ...head,
    type: 'FamilyConfigured',
    payload: {
      familyId: options.familyId,
      timezone: options.timezone,
      dayCutoffHour: options.dayCutoffHour,
      meetingDayOfWeek: options.meetingDayOfWeek,
    },
  })

  events.push(adultEvent({ id: options.parentId, name: options.parentName }, head))
  for (const adulto of options.otherAdults ?? []) {
    events.push(adultEvent(adulto, head))
  }

  for (const child of options.children) {
    events.push(...childEvents(child, head))
  }

  const childIds = options.children.map((c) => c.id)
  for (const reward of REWARDS) {
    events.push({
      ...head,
      type: 'RewardDefined',
      payload: {
        rewardId: `r-${reward.key}` as RewardId,
        name: reward.name,
        price: reward.price,
        kind: reward.kind,
        memberIds: childIds,
        ...(reward.notes !== undefined ? { notes: reward.notes } : {}),
        ...(reward.limit !== undefined ? { limit: reward.limit } : {}),
      },
    })
  }

  return events
}
